// ---------------------------------------------------------------------------
// Estado do dashboard
// ---------------------------------------------------------------------------
export type DashboardState =
  | 'INGESTING'
  | 'INGEST_FAILED'
  | 'EMPTY_WITH_OAB'
  | 'EMPTY_NO_OAB'
  | 'NORMAL';

// ---------------------------------------------------------------------------
// CSS para animações do dashboard
// ---------------------------------------------------------------------------
export const dashboardAnimationStyles = `
@keyframes dashIndeterminate {
  from { transform: translateX(-100%); }
  to { transform: translateX(300%); }
}
.animate-dash-indeterminate { animation: dashIndeterminate 1.5s ease-in-out infinite; }

@keyframes dashFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-dash-fade-in { animation: dashFadeIn 400ms ease-out; }

@keyframes dashScaleIn {
  from { transform: scale(0); }
  50% { transform: scale(1.1); }
  to { transform: scale(1); }
}
.animate-dash-scale-in { animation: dashScaleIn 600ms ease-out; }
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
export function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

/**
 * O primeiro nome para a saudação, em caixa normal: o cadastro vem muitas vezes em
 * maiúsculas ("MARIMAR PEREIRA" → "Marimar"); nomes compostos por hífen também
 * ("ANA-LUÍSA" → "Ana-Luísa").
 */
export function primeiroNome(nome?: string | null): string {
  const primeiro = (nome ?? '').trim().split(/\s+/)[0] ?? '';
  return primeiro
    .toLocaleLowerCase('pt-BR')
    .split('-')
    .map((parte) => parte.charAt(0).toLocaleUpperCase('pt-BR') + parte.slice(1))
    .join('-');
}

export function formatFullDate(): string {
  const now = new Date();
  const weekdays = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
  ];
  return `${weekdays[now.getDay()]}, ${now.getDate()} de ${months[now.getMonth()]} de ${now.getFullYear()}`;
}

export function formatRelativeTime(dateStr?: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min atrás`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h atrás`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `${diffD}d atrás`;
  return date.toLocaleDateString('pt-BR');
}

export function formatOabLabel(oab: { numero: string; uf: string } | undefined): string {
  if (!oab) return '';
  return ' para OAB/' + oab.uf + ' ' + oab.numero;
}

export function getSyncStatusColor(dateStr: string | null): { bg: string; color: string; label: string } {
  if (!dateStr) return { bg: 'bg-muted', color: 'text-muted-foreground', label: 'Nunca sincronizado' };
  const diffH = (Date.now() - new Date(dateStr).getTime()) / 3600000;
  if (diffH < 24) return { bg: 'bg-emerald-500/10', color: 'text-emerald-500', label: 'Atualizado' };
  if (diffH < 48) return { bg: 'bg-yellow-500/10', color: 'text-yellow-500', label: 'Desatualizado' };
  return { bg: 'bg-red-500/10', color: 'text-red-500', label: 'Desatualizado' };
}
